import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import FlightSearchAirportModal from './FlightAirportSelect/FlightSearchAirportModal'
import FlightSearchResponsiveAirportField from './FlightAirportSelect/FlightSearchResponsiveAirportField'
import ModalContext from 'contexts/ModalContext'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import FlightSearchMenuMobile from './FlightSearchMenu/FlightSearchMenuMobile'
import { addDays } from '@luxuryescapes/lib-refunds/build/lib/date'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import FlightSearchTravellerSelectContent from './FlightTravellerSelect/FlightSearchTravellerSelectContent'
import { getAirportDisplayText } from 'lib/flights/flightUtils'
import useFeaturedFlightDeals from 'hooks/Flights/useFeaturedFlightDeals'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import FlightDealDuration from 'components/Flights/FlightMerchandising/FlightDealDuration/FlightDealDuration'
import FlightDealAgentHubDisclaimer from 'components/Flights/FlightMerchandising/FlightDealAgentHubDisclaimer/FlightDealAgentHubDisclaimer'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import BodyText from 'LuxKit/Typography/BodyText'
import { FLIGHTS_MAX_LEAD_TIME_DAYS, FLIGHTS_MIN_LEAD_TIME_DAYS, FlightsFareTypes } from 'constants/flight'
import styled from 'styled-components'
import { rem } from 'polished'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import type { FlightSearchWidgetInitialFocus } from 'contexts/Flights/FlightSearchWidget/types'
import type { SelectingDateType } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { StandaloneFlightMenu } from 'contexts/Flights/FlightSearchWidget/types'
import IconButton from 'LuxKit/Button/IconButton'
import LineArrowsSwapVerticalIcon from 'LuxKit/Icons/line/LineArrowsSwapVerticalIcon'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import FlightsVerticalDatePicker from './FlightsDatePicker/FlightsVerticalDatePicker'
import FlightsResultsDatePicker from './FlightsDatePicker/FlightsResultsDatePicker'
import FlightSearchDateSelectSheet from './FlightSearchDateSelectSheet'
import { applyFlightSearchInitialFocus } from './applyFlightSearchInitialFocus'
import clsx from 'clsx'

const ButtonsContainer = styled(Group)`
  &.mobile-results-redesign {
    overflow: hidden;
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-radius: ${props => props.theme.borderRadius.L};
  }

  &.mobile-results-redesign .airport-select {
    align-self: stretch;
    background-color: transparent;
    border: 0;
    border-block-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-radius: 0;
    box-sizing: border-box;
    gap: 0;
    min-block-size: ${rem(48)};
    padding-block: ${rem(12)};
    padding-inline: ${rem(16)};
  }

  &.mobile-results-redesign .dates > * {
    background-color: transparent;
    border: 0;
    border-radius: 0;
  }

  &.mobile-results-redesign .dates > * + * {
    border-inline-start: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &.mobile-results-redesign .dates > .no-label {
    align-self: stretch;
    box-sizing: border-box;
    min-block-size: ${rem(48)};
    padding-block: ${rem(12)};
    padding-inline: ${rem(16)};
  }

  & > button:not(:last-child) {
    border-bottom: 0;
  }
`

const AirportsContainer = styled(Group)`
  position: relative;

  ${mediaQueryDown.mobile} {
    .airport-select{
      border-bottom: 0;
    }
  }

  & > button:not(.switch){
    border-bottom: 0;
  }

  &.mobile-results-redesign > button {
    min-height: ${rem(48)};
  }
`

const ExchangeButtonContainer = styled.div`
  position: absolute;
  inset-inline-end: ${rem(16)};
  top: 50%;
  transform: translateY(-50%);
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: 50%;
`

interface Props {
  /**
   * Sets the airports to a 'read only' mode
   * Used when defaulting airport selections such as on flight deal pages
   */
  readOnlyAirports?: boolean
  initialFocus?: FlightSearchWidgetInitialFocus
  mobileResultsRedesign?: boolean
}

function FlightSearchWidgetMobileForm(props: Props) {
  const { readOnlyAirports, initialFocus, mobileResultsRedesign } = props
  const intl = useIntl()
  const state = useContext(FlightSearchWidgetStateContext)

  const {
    fareType,
    flightDealId,
    flights,
  } = state

  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const hasAppliedInitialFocus = useRef(false)
  const datePickerAnchorRef = useRef<HTMLDivElement>(null)
  const isTabletUp = useScreenSizeUp('tablet')
  const isTabletViewport = isTabletUp || (typeof window !== 'undefined' && window.matchMedia(mediaQueryUp.tablet).matches)

  const { showSegmentActiveDeals } = state

  const flight = flights[0]
  const [flightDeals] = useFlightDeals()

  const showModal = useContext(ModalContext)

  const travellersTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'M-8hnlubARNYJJXZe8dP',
        defaultMessage: 'Travellers',
        description: 'Title for travellers selection modal in flight search widget',
      }),
    [intl],
  )

  const onTravellerSelect = useCallback(() => {
    showModal(<FlightSearchMenuMobile
      title={travellersTitle}
      showTravellers
    >
      <FlightSearchTravellerSelectContent />
    </FlightSearchMenuMobile>)
  }, [showModal, travellersTitle])

  const earliestBookingDate = useMemo(() => addDays(new Date(), FLIGHTS_MIN_LEAD_TIME_DAYS), [])
  const finalBookingDate = useMemo(() => addDays(new Date(), FLIGHTS_MAX_LEAD_TIME_DAYS), [])
  const { featuredRanges, displayDeal } = useFeaturedFlightDeals({
    flightDeals,
    departureAirport: flight.departureAirport,
    arrivalAirport: flight.arrivalAirport,
    fareType: fareType.value,
    showSegmentActiveDeals,
    flightDealId,
  })

  const dateSelectTitle = useMemo(
    () => mobileResultsRedesign ? intl.formatMessage({
      id: 'xcIuQafq7GU0ucPlHFHV',
      defaultMessage: 'Select dates',
      description: 'Title for the redesigned mobile and tablet flights date selection modal',
    }) : intl.formatMessage({
      id: 'C36_0JIKVCqvkVWZ8_o-',
      defaultMessage: 'When do you want to go?',
      description: 'Title for date selection modal in flight search widget',
    }),
    [intl, mobileResultsRedesign],
  )

  const dateSelectPriceInfo = useMemo(
    () =>
      intl.formatMessage({
        id: 'i0m0YZ8D02Pv9H6IaekD',
        defaultMessage: 'Based on recent return prices (per adult) found',
        description: 'Price information text shown in date selection modal',
      }),
    [intl],
  )

  const onDateSelect = useCallback(async(selectingDateType: SelectingDateType = 'departing') => {
    if (mobileResultsRedesign && isTabletViewport) {
      dispatch({
        type: FlightSearchWidgetActions.SET_ACTIVE_MENU,
        id: flight.id,
        activeMenu: StandaloneFlightMenu.date,
        selectingDateType,
      })
      return
    }

    // Set the selectingDateType in state before opening the modal
    dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, selectingDateType })

    const dates = await showModal<Record<string, string>>(<FlightSearchMenuMobile
      title={dateSelectTitle}
      showDates
      useApplyActions={mobileResultsRedesign}
    >
      <Group direction="vertical" verticalAlign="space-between" gap={12} fullHeight>
        <VerticalSpacer gap={12}>
          {displayDeal && !isAgentHubEnabled && <FlightDealDuration flightDeal={displayDeal} />}
          {isAgentHubEnabled && <FlightDealAgentHubDisclaimer />}
          <BodyText variant="small" colour="light-foreground-alt">
            {dateSelectPriceInfo}
          </BodyText>
        </VerticalSpacer>

        {mobileResultsRedesign ? <FlightsResultsDatePicker
          minDate={earliestBookingDate}
          maxDate={finalBookingDate}
          id={flight.id}
          featuredRanges={featuredRanges}
        /> : <FlightsVerticalDatePicker
          minDate={earliestBookingDate}
          maxDate={finalBookingDate}
          id={flight.id}
          required
          featuredRanges={featuredRanges}
        />}
      </Group>
    </FlightSearchMenuMobile>,
    )

    if (dates && !mobileResultsRedesign) {
      onTravellerSelect()
    }
  }, [showModal, earliestBookingDate, finalBookingDate, flight.id, featuredRanges, displayDeal, mobileResultsRedesign, onTravellerSelect, dateSelectTitle, dateSelectPriceInfo, dispatch, isTabletViewport])

  const closeTabletDatePicker = useCallback(() => {
    dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, activeMenu: undefined })
  }, [dispatch])

  const onDepartingDateSelect = useCallback(() => {
    onDateSelect('departing')
  }, [onDateSelect])

  const onReturningDateSelect = useCallback(() => {
    onDateSelect('returning')
  }, [onDateSelect])

  const onArrivalSelect = useCallback(async() => {
    if (mobileResultsRedesign && isTabletViewport) {
      dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, id: flight.id, activeMenu: StandaloneFlightMenu.arrivalAirport })
      return
    }
    const airport = await showModal<App.AirportLocation | undefined>(
      <FlightSearchAirportModal direction="arrival" id={flight.id} />,
    )
    if (airport) {
      onDateSelect('departing')
    }
  }, [onDateSelect, showModal, flight.id, mobileResultsRedesign, isTabletViewport, dispatch])

  const onDepartureSelect = useCallback(async() => {
    if (mobileResultsRedesign && isTabletViewport) {
      dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, id: flight.id, activeMenu: StandaloneFlightMenu.departAirport })
      return
    }
    const airport = await showModal<App.AirportLocation | undefined>(
      <FlightSearchAirportModal direction="departure" id={flight.id} />,
    )
    if (airport) {
      onArrivalSelect()
    }
  }, [onArrivalSelect, showModal, flight.id, mobileResultsRedesign, isTabletViewport, dispatch])

  const onSwapAirports = useCallback((flightId: string) => {
    const currentFlight = flights.find(flight => flight.id === flightId)
    if (!currentFlight) return
    const departure = currentFlight.departureAirport
    dispatch({ type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT, id: flightId, airport: currentFlight.arrivalAirport })
    dispatch({ type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT, id: flightId, airport: departure })
  }, [flights, dispatch])

  useEffect(() => {
    if (!initialFocus || hasAppliedInitialFocus.current || initialFocus.flightId !== flight.id) {
      return
    }

    const timeoutId = setTimeout(() => {
      hasAppliedInitialFocus.current = true
      applyFlightSearchInitialFocus(initialFocus, {
        departureAirport: onDepartureSelect,
        arrivalAirport: onArrivalSelect,
        departureDate: onDepartingDateSelect,
        returnDate: onReturningDateSelect,
      })
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [flight.id, initialFocus, onArrivalSelect, onDepartingDateSelect, onDepartureSelect, onReturningDateSelect])

  return <div ref={datePickerAnchorRef}>
    <ButtonsContainer
      className={clsx({ 'mobile-results-redesign': mobileResultsRedesign })}
      direction="vertical"
    >
      <AirportsContainer
        className={clsx({ 'mobile-results-redesign': mobileResultsRedesign })}
        direction="vertical"
      >
        <FlightSearchResponsiveAirportField
          inlineSearch={!!mobileResultsRedesign && isTabletViewport}
          flightId={flight.id}
          direction="departure"
          readOnly={readOnlyAirports}
          onNext={onArrivalSelect}
          value={getAirportDisplayText(flight.departureAirport)}
          label={mobileResultsRedesign ? undefined : intl.formatMessage({
            id: 'ZGF6ICItXlqbkxqPyUHc',
            defaultMessage: 'From',
            description: 'Label for departure airport field in flight search',
          })}
          aria-label={intl.formatMessage({
            id: 'ZGF6ICItXlqbkxqPyUHc',
            defaultMessage: 'From',
            description: 'Label for departure airport field in flight search',
          })}
          required
          placeholder={intl.formatMessage({
            id: 'HRi6-dtcAk0WQRsHbA_y',
            defaultMessage: 'Search by city or airport',
            description: 'Placeholder text for airport search field in the search interface',
          })}
          onClick={readOnlyAirports ? undefined : onDepartureSelect}
          requiredErrorMessage={intl.formatMessage({
            id: 'XT7iAbC3Uf_TMuFN09pG',
            defaultMessage: 'Please enter a departure',
            description: 'Error message when departure airport is not selected',
          })}
          hasError={flight.showDepartureError}
          className="airport-select"
        />

        <FlightSearchResponsiveAirportField
          inlineSearch={!!mobileResultsRedesign && isTabletViewport}
          flightId={flight.id}
          direction="arrival"
          readOnly={readOnlyAirports}
          onNext={onDepartingDateSelect}
          value={getAirportDisplayText(flight.arrivalAirport)}
          label={mobileResultsRedesign ? undefined : intl.formatMessage({
            id: '2UOCctw7EHd0XUmxtgr3',
            defaultMessage: 'To',
            description: 'Form field label for arrival airport field in flight search',
          })}
          aria-label={intl.formatMessage({
            id: '2UOCctw7EHd0XUmxtgr3',
            defaultMessage: 'To',
            description: 'Form field label for arrival airport field in flight search',
          })}
          required
          placeholder={intl.formatMessage({
            id: 'HRi6-dtcAk0WQRsHbA_y',
            defaultMessage: 'Search by city or airport',
            description: 'Placeholder text for airport search field in the search interface',
          })}
          onClick={readOnlyAirports ? undefined : onArrivalSelect}
          requiredErrorMessage={intl.formatMessage({
            id: '0gkTK6pcWoE5eD7RD9w7',
            defaultMessage: 'Please enter a destination',
            description: 'Error message when destination airport is not selected',
          })}
          hasError={flight.showDestinationError}
          className="airport-select"
        />

        <ExchangeButtonContainer>
          <IconButton variant="default" kind="tertiary" size="medium" shape="circle" className="switch" onClick={() => onSwapAirports(flight.id)} disabled={readOnlyAirports}>
            <LineArrowsSwapVerticalIcon size="M" />
          </IconButton>
        </ExchangeButtonContainer>
      </AirportsContainer>

      <SearchFormFieldGroup className="dates">
        <SearchFormField
          label={mobileResultsRedesign ? undefined : intl.formatMessage({
            id: 'P7H8A2BFmIUBImUgMwMg',
            defaultMessage: 'Departing',
            description: 'Form field label for departure date field in flight search',
          })}
          aria-label={intl.formatMessage({
            id: 'P7H8A2BFmIUBImUgMwMg',
            defaultMessage: 'Departing',
            description: 'Form field label for departure date field in flight search',
          })}
          placeholder={intl.formatMessage({
            id: 'Xnhjjfb2SQAvUwMEDgTX',
            defaultMessage: 'Select date',
            description: 'Placeholder text for date selection field when field is empty',
          })}
          required
          requiredErrorMessage={intl.formatMessage({
            id: 'QjCDP-dTWZM0XJzm4nhw',
            defaultMessage: 'Please select travel dates',
            description: 'Error notification displayed when travel dates are not selected',
          })}
          value={flight.checkinDate ? intlDateFormatter(flight.checkinDate, INTL_DMY_CASUAL_FORMAT, intl.locale) : undefined}
          onClick={onDepartingDateSelect}
          hasError={flight.showTravelDatesError}
        />

        {fareType.value === FlightsFareTypes.RETURN && <SearchFormField
          label={mobileResultsRedesign ? undefined : intl.formatMessage({
            id: '2onW6uKWgu7ObnjMNd0L',
            defaultMessage: 'Returning',
            description: 'Label for return date field in flight search in the flights flow',
          })}
          aria-label={intl.formatMessage({
            id: '2onW6uKWgu7ObnjMNd0L',
            defaultMessage: 'Returning',
            description: 'Label for return date field in flight search in the flights flow',
          })}
          placeholder={intl.formatMessage({
            id: 'Xnhjjfb2SQAvUwMEDgTX',
            defaultMessage: 'Select date',
            description: 'Placeholder text for date selection field when field is empty',
          })}
          required
          requiredErrorMessage={intl.formatMessage({
            id: 'QjCDP-dTWZM0XJzm4nhw',
            defaultMessage: 'Please select travel dates',
            description: 'Error notification displayed when travel dates are not selected',
          })}
          value={flight.checkoutDate ? intlDateFormatter(flight.checkoutDate, INTL_DMY_CASUAL_FORMAT, intl.locale) : undefined}
          onClick={onReturningDateSelect}
          hasError={flight.showTravelDatesError}
        />}
      </SearchFormFieldGroup>
    </ButtonsContainer>
    {mobileResultsRedesign && isTabletViewport && <FlightSearchDateSelectSheet
      id={flight.id}
      anchorRef={datePickerAnchorRef}
      isOpen={state.activeMenu === StandaloneFlightMenu.date}
      onClose={closeTabletDatePicker}
      flightDeals={flightDeals}
      mobileResultsRedesign
    />}
  </div>
}

export default FlightSearchWidgetMobileForm
